import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useEffect, useRef, type ReactNode } from "react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { TopLoadingBar } from "@/components/skeletons";
import { isTenantContextRequiredError, useTenant } from "@/hooks/useTenant";
import { getTenantSubdomain, isTenantRootDomain } from "@/lib/tenant";
import { recoverFromInvalidTenant } from "@/lib/tenantRecovery";
import { ChatHistoryProvider } from "@/contexts/ChatHistoryContext";
import { AuthBootstrap } from "@/components/AuthBootstrap";

// Eagerly loaded pages (core app flow)
import Index from "./pages/Index";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";
import CompanyAdminRoute from "./components/CompanyAdminRoute";
import AdminRoute from "./components/AdminRoute";

// Lazy loaded pages (code splitting for performance)
const MeetingDashboard = lazy(() => import("./pages/MeetingDashboard"));
const Upload = lazy(() => import("./pages/Upload"));
const Record = lazy(() => import("./pages/Record"));
const MeetingBot = lazy(() => import("./pages/MeetingBot"));
const MeetingAudioInbox = lazy(() => import("./pages/MeetingAudioInbox"));
const Meeting = lazy(() => import("./pages/Meeting"));
const AIAgent = lazy(() => import("./pages/AIAgent"));
const StaffInsights = lazy(() => import("./pages/StaffInsights"));
const NewTask = lazy(() => import("./pages/NewTask"));
const AgentTasks = lazy(() => import("./pages/AgentTasks"));
const Projects = lazy(() => import("./pages/Projects"));
const Documents = lazy(() => import("./pages/Documents"));
const Contact = lazy(() => import("./pages/Contact"));
const Integrations = lazy(() => import("./pages/Integrations"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminCompanies = lazy(() => import("./pages/AdminCompanies"));
const AdminTrialSettings = lazy(() => import("./pages/AdminTrialSettings"));
const AdminCredits = lazy(() => import("./pages/AdminCredits"));
const AdminAffiliate = lazy(() => import("./pages/AdminAffiliate"));
const AdminPromotionCodes = lazy(() => import("./pages/AdminPromotionCodes"));
const AdminCustomers = lazy(() => import("./pages/AdminCustomers"));
const Affiliate = lazy(() => import("./pages/Affiliate"));
const CompanyUsers = lazy(() => import("./pages/CompanyUsers"));
const CompanyRoles = lazy(() => import("./pages/CompanyRoles"));
const CompanySettings = lazy(() => import("./pages/CompanySettings"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const PromotionCodes = lazy(() => import("./pages/PromotionCodes"));
const CompanyUsage = lazy(() => import("./pages/CompanyUsage"));
const UserCredits = lazy(() => import("./pages/UserCredits"));
const CompanyCredits = lazy(() => import("./pages/CompanyCredits"));
const CompanyStorage = lazy(() => import("./pages/CompanyStorage"));
const Billing = lazy(() => import("./pages/Billing"));
const BillingRedirect = lazy(() => import("./pages/BillingRedirect"));
const CompanyApprovals = lazy(() => import("./pages/CompanyApprovals"));
const DeveloperApiKeys = lazy(() => import("./pages/DeveloperApiKeys"));
const MeetingSettings = lazy(() => import("./pages/MeetingSettings"));
const PrintReport = lazy(() => import("./pages/PrintReport"));
const PrintAgentReport = lazy(() => import("./pages/PrintAgentReport"));
const RootDomainRedirect = lazy(() => import("./pages/RootDomainRedirect"));
const ArtifactViewer = lazy(() => import("./pages/ArtifactViewer"));

// Thin top progress bar fallback — keeps the user's previous view in place
// and surfaces a subtle motion cue while the next chunk/route is fetching.
const PageLoader = () => (
  <div className="fixed inset-x-0 top-0 z-[60]">
    <TopLoadingBar className="h-1 rounded-none" />
  </div>
);

const queryClient = new QueryClient();

import { Layout } from "./components/Layout";

const TenantGate = ({ children }: { children: ReactNode }) => {
  const hostname = window.location.hostname;
  const onRootDomain = isTenantRootDomain(hostname);
  const { data: tenant, isPending, isError, error } = useTenant();
  const tenantSubdomain = getTenantSubdomain(hostname);
  const recoveryStarted = useRef(false);

  const isInvalidTenant =
    Boolean(tenantSubdomain) &&
    !isPending &&
    (tenant === null || (isError && isTenantContextRequiredError(error)));

  useEffect(() => {
    if (!isInvalidTenant || recoveryStarted.current) {
      return;
    }
    recoveryStarted.current = true;
    void recoverFromInvalidTenant({ reason: "workspace_not_found" });
  }, [isInvalidTenant]);

  if (onRootDomain) {
    return <>{children}</>;
  }

  if (tenantSubdomain && isPending) {
    return <PageLoader />;
  }

  if (isInvalidTenant) {
    return <PageLoader />;
  }

  return <>{children}</>;
};

const PublicRegisterRoute = () => {
  if (getTenantSubdomain(window.location.hostname)) {
    return <NotFound />;
  }

  return <Register />;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AuthBootstrap>
          <BrowserRouter>
            <TenantGate>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<PublicRegisterRoute />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-use" element={<TermsOfUse />} />
                  <Route
                    path="/billing/success"
                    element={
                      <ProtectedRoute>
                        <BillingRedirect outcome="success" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/billing/cancel"
                    element={
                      <ProtectedRoute>
                        <BillingRedirect outcome="cancel" />
                      </ProtectedRoute>
                    }
                  />

                  {/* Authenticated Routes wrapped in Layout */}
                  <Route
                    path="/meetings"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <MeetingDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/upload"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Upload />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/record"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Record />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/meetings/audio-inbox"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <MeetingAudioInbox />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/meeting-bot"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <MeetingBot />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/meeting/:id"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Meeting />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/ai-agents/insights"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <StaffInsights />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/ai-agents/departments/:departmentId"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <StaffInsights />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/new-task"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <NewTask />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/auto-tasks"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AgentTasks />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/projects"
                    element={
                      <ProtectedRoute>
                        <ChatHistoryProvider>
                          <Layout>
                            <Projects />
                          </Layout>
                        </ChatHistoryProvider>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/ai-agents/*"
                    element={
                      <ProtectedRoute>
                        <ChatHistoryProvider>
                          <Layout>
                            <AIAgent />
                          </Layout>
                        </ChatHistoryProvider>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/workspace"
                    element={
                      <ProtectedRoute>
                        <RootDomainRedirect />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/documents"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Documents />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contact"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Contact />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/integrations"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Integrations />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/meeting-settings"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <MeetingSettings />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/artifacts/:artifactId"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ArtifactViewer />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Admin />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/developer/api-keys"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <DeveloperApiKeys />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/customers"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <Layout>
                            <AdminCustomers />
                          </Layout>
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AdminUsers />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/companies"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AdminCompanies />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/trial-settings"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AdminTrialSettings />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/credits"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AdminCredits />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/affiliate"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AdminAffiliate />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/promotion-codes"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AdminPromotionCodes />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/affiliate"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Affiliate />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/users"
                    element={
                      <ProtectedRoute>
                        <CompanyAdminRoute>
                          <Layout>
                            <CompanyUsers />
                          </Layout>
                        </CompanyAdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/account"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AccountSettings />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/account/promotion-codes"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <PromotionCodes />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/settings"
                    element={
                      <ProtectedRoute>
                        <CompanyAdminRoute>
                          <Layout>
                            <CompanySettings />
                          </Layout>
                        </CompanyAdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/roles"
                    element={
                      <ProtectedRoute>
                        <CompanyAdminRoute>
                          <Layout>
                            <CompanyRoles />
                          </Layout>
                        </CompanyAdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/usage"
                    element={
                      <ProtectedRoute>
                        <CompanyAdminRoute>
                          <Layout>
                            <CompanyUsage />
                          </Layout>
                        </CompanyAdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my/credits"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <UserCredits />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/credits"
                    element={
                      <ProtectedRoute>
                        <CompanyAdminRoute>
                          <Layout>
                            <CompanyCredits />
                          </Layout>
                        </CompanyAdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/storage"
                    element={
                      <ProtectedRoute>
                        <CompanyAdminRoute>
                          <Layout>
                            <CompanyStorage />
                          </Layout>
                        </CompanyAdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/billing"
                    element={
                      <ProtectedRoute>
                        <CompanyAdminRoute>
                          <Layout>
                            <Billing />
                          </Layout>
                        </CompanyAdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/company/approvals"
                    element={
                      <ProtectedRoute>
                        <CompanyAdminRoute>
                          <Layout>
                            <CompanyApprovals />
                          </Layout>
                        </CompanyAdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  {/* Print routes - public, token-based auth (used by Playwright for PDF generation) */}
                  <Route path="/report/print/:id" element={<PrintReport />} />
                  <Route path="/report/agent-print/:id" element={<PrintAgentReport />} />

                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </TenantGate>
          </BrowserRouter>
        </AuthBootstrap>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
